/**
 * G20 — painting the ground under a wood the canopy green, so a wood reads as
 * a wood from patrol height instead of as speckles.
 *
 * ## The measurement this exists for
 *
 * `docs/engineering/enhanced-terrain.md` §8a: at 55° over an 800 px view a wood
 * is **109 px across** from 6 000 ft, so it is not too small — it is too
 * *empty*. `WOOD_TREES` (26) crowns of `CANOPY_RADIUS_M` (4.677 m) inside a
 * `WOOD_RADIUS_M` (130 m) disc is **3.36 % canopy closure**, against European
 * mixed woodland's 60-90 %: about 327 px² of tree scattered over a 9 331 px²
 * patch. In frame at 2 890 ft that reads as dark green dots on the fields and
 * at 740 ft as individual trees standing about on open grass.
 *
 * The three ways out were priced there and the user picked the first
 * (2026-08-26): **paint the ground**, and leave the trees to supply texture
 * close up. It is the only one that does not touch a measured number — tree
 * size, tree count, wood radius and site density are all exactly what they
 * were — and from 6 000 ft a dark green patch is what a wood actually looks
 * like.
 *
 * ## Why this adds no surface, and what that buys
 *
 * The obvious build is a green disc laid over the field. **It is the wrong
 * build here**, and `plate-paint-order.ts` is why: this app's ground is already
 * a stack of plates authored at one datum, and every coplanar pair in it is a
 * tie the depth buffer cannot arbitrate and the camera therefore decides
 * (the 2026-08-26 "brown suddenly switches to green" report). B3 is the same
 * bug one storey up, for the hairline network. A disc per wood would be 400 new
 * ground-coplanar surfaces per region wanting a rung on that ladder, and the
 * only rungs left are between the plate's own top and `HAIRLINE_LIFT_M` — 2 mm,
 * inside which nothing can be separated by more than the depth buffer's own
 * quantum at range.
 *
 * So there is no new surface. **The paint is the plate's own vertices**: this
 * module rewrites the `color` attribute of faces the landcover already owns and
 * touches `position` not at all. Zero draw calls, zero triangles, zero new
 * coplanar pairs — `coplanarPlatePairs` is provably unchanged, because it reads
 * only positions, and `test/wood-ground-paint.test.ts` asserts the position
 * buffer is bit-identical across a paint. That is the whole of this lane's
 * answer to the ordering question, and it is an answer by construction rather
 * than by a chosen clearance.
 *
 * The price is **resolution**: the paint is quantised to the faces
 * `terrain-drape.ts` cut the plate into, which are the relief mesh's own cells —
 * ~104 m under the aeroplane, ~150 m at 5 km, ~375 m twenty kilometres out
 * (`relief-ground.ts`'s graded lattice). A 130 m-radius wood therefore paints
 * roughly ten faces near the pilot and one or two at the rim. The test pins
 * both the count and the area ratio; the honest summary is that a near wood is
 * a blocky disc and a far one is a single cell of about the right area, which
 * is a distinction no pilot can see at the range it applies.
 *
 * ## Which faces, and which green
 *
 * **Only faces the game itself paints foliage-green.** `terrain-decoration.ts`
 * §2's whole interpretation is that `BARON.PAL`'s four green landcover entries
 * are where a wood is *plausible*, because the shipped tree object's own
 * canopies are painted two of them. A wood site is drawn from exactly those
 * polygons, so restricting the paint to them costs nothing and makes it
 * impossible for a wood that overhangs a river, a road block or a ploughed
 * field to repaint it. Compared against the entries **as the hour left them**,
 * since `world-shared-ref.ts` has already walked the plate down its ramp by
 * `rampShift` before this runs.
 *
 * **A face is painted when its centroid is inside a wood disc, or when a wood's
 * centre is inside the face.** The second clause is not a refinement, it is the
 * census: a site is a barycentric sample of a green landcover triangle, so
 * after the drape's cut it lies inside one of that triangle's pieces, which
 * carries the same colour — and therefore *every* wood paints at least one
 * face, however coarse the lattice is under it. Without it a wood smaller than
 * the cell it sits in paints nothing, which is precisely the far half of the
 * region.
 *
 * **The green is `CANOPY_RUN[0]`, `#2c4520`** — a real `BARON.PAL` entry, the
 * bottom of the same green run the game's own canopies live on and the step
 * `tree-geometry.ts` already uses for a lobe's shaded underside. It is darker
 * than all four field greens, which is the point; and it is not invented, which
 * is the other point. `terrain-palette.ts` independently reaches the same
 * colour from the other side: its `LANDCOVER_NIGHT_LADDER` dims both canopy
 * entries `#516534` and `#556134` to `#2c4520`. The hour walks it with
 * `dimTerrainColour`, the same function the plate under it was walked by, so
 * the wood and the field darken together.
 */

import type * as THREE from "three/webgpu";
import { CANOPY_RUN } from "./tree-geometry.ts";
import { FOLIAGE_GROUND_COLOURS, WOOD_RADIUS_M, type TreeSite } from "./terrain-decoration.ts";
import { dimTerrainColour } from "../world/terrain-palette.ts";

/**
 * The ground tone under a canopy at noon: `#2c4520`, the foot of `CANOPY_RUN`.
 * See the module doc for why it is this entry and not a manufactured tint.
 */
export const CANOPY_GROUND_COLOUR: number = CANOPY_RUN[0]!;

/** `world/landcover-shading.ts`'s shader, described structurally so this module stays a leaf. */
export type FaceShader = (tone: number, normalX: number, normalY: number, normalZ: number) => number;

/**
 * One plate's canopy paint, sparse: the faces to repaint and the nine floats
 * each of them takes.
 *
 * Sparse rather than a fourth whole-plate colour array because a region's
 * plates run to hundreds of thousands of faces and the painted share is under a
 * percent of them — and because keeping it separate is what lets the paint be
 * applied *after* the flat/shaded swap rather than baked into one of them, so
 * the two effects stay independently nameable and independently testable.
 */
export interface WoodGroundPaint {
  /** Face indices into the plate's non-indexed attributes — nine floats each. */
  readonly faces: Uint32Array;
  /** Nine floats per entry of `faces`, in the same order. */
  readonly colours: Float32Array;
  /** One byte per wood site: 1 where this plate carries some of that wood's paint. */
  readonly sitesPainted: Uint8Array;
  /** The tone actually laid down, after the hour's walk. */
  readonly tone: number;
}

/** Side of the site-lookup grid. `WOOD_RADIUS_M`, so a disc never spans more than two cells on an axis. */
const SITE_CELL_M = WOOD_RADIUS_M;

function cellKey(ix: number, iz: number): string {
  return `${ix}|${iz}`;
}

/** Wood sites bucketed by `SITE_CELL_M` cell, so a face queries its own neighbourhood instead of all 400. */
function siteGrid(sites: readonly TreeSite[]): Map<string, number[]> {
  const grid = new Map<string, number[]>();
  sites.forEach((site, index) => {
    const key = cellKey(Math.floor(site.x / SITE_CELL_M), Math.floor(site.z / SITE_CELL_M));
    const bucket = grid.get(key);
    if (bucket) bucket.push(index);
    else grid.set(key, [index]);
  });
  return grid;
}

/**
 * True when `(px, pz)` is inside triangle `a b c` in XZ.
 *
 * Sign-agnostic, because a plate's own winding is not a promise about which way
 * it faces — the same reason `terrain-drape.ts` and `plate-paint-order.ts` both
 * take "up" as given rather than reading it off the geometry. The epsilon is
 * relative to the triangle's own area so that a point exactly on an edge lands
 * in at least one piece rather than in none, which is what makes the census
 * clause a guarantee instead of a near-certainty.
 */
function pointInTriangle(
  px: number, pz: number,
  ax: number, az: number,
  bx: number, bz: number,
  cx: number, cz: number,
): boolean {
  const area2 = (bx - ax) * (cz - az) - (cx - ax) * (bz - az);
  if (area2 === 0) return false;
  // `p = a + s(b - a) + t(c - a)`, solved directly. Dividing by the signed
  // `area2` is what makes it winding-agnostic: both orientations give the same
  // barycentric pair.
  const dx = px - ax;
  const dz = pz - az;
  const s = (dx * (cz - az) - dz * (cx - ax)) / area2;
  const t = ((bx - ax) * dz - (bz - az) * dx) / area2;
  const epsilon = 1e-9;
  return s >= -epsilon && t >= -epsilon && s + t <= 1 + epsilon;
}

/**
 * The canopy paint for one already-draped plate, or `undefined` when the plate
 * carries no foliage-green face inside any wood.
 *
 * Runs **after** `drapeGeometry` and for the same reason `shadedPlateColours`
 * does: before the cut a plate face spans several ground cells and has neither
 * a single slope to shade by nor a footprint small enough for "is this face
 * under a wood" to mean anything.
 *
 * `originX`/`originZ` are the placement's own translation — the geometry is
 * still in its object frame and the wood sites are world space.
 */
export function woodGroundPaintFor(
  geometry: THREE.BufferGeometry,
  originX: number,
  originZ: number,
  sites: readonly TreeSite[],
  shade: FaceShader,
  rampShift: number,
): WoodGroundPaint | undefined {
  const position = geometry.getAttribute("position");
  const colour = geometry.getAttribute("color");
  if (!position || !colour || sites.length === 0) return undefined;

  const p = position.array as ArrayLike<number>;
  const source = colour.array as ArrayLike<number>;
  // The greens as the hour left them: `world-shared-ref.ts` dimmed the plate
  // before this ran, so comparing against the noon entries would find nothing
  // after dusk.
  const greens = new Set(FOLIAGE_GROUND_COLOURS.map((entry) => dimTerrainColour(entry, rampShift)));
  const tone = dimTerrainColour(CANOPY_GROUND_COLOUR, rampShift);
  const grid = siteGrid(sites);
  const sitesPainted = new Uint8Array(sites.length);

  const faces: number[] = [];
  const colours: number[] = [];

  for (let t = 0; t + 8 < p.length; t += 9) {
    const packed =
      (Math.round(source[t]! * 255) << 16) | (Math.round(source[t + 1]! * 255) << 8) | Math.round(source[t + 2]! * 255);
    if (!greens.has(packed)) continue;

    const ax = p[t]! + originX, az = p[t + 2]! + originZ;
    const bx = p[t + 3]! + originX, bz = p[t + 5]! + originZ;
    const cx = p[t + 6]! + originX, cz = p[t + 8]! + originZ;
    const centroidX = (ax + bx + cx) / 3;
    const centroidZ = (az + bz + cz) / 3;

    // The face's own bounding box, grown by a wood radius: a rim cell is wider
    // than `SITE_CELL_M`, so a fixed neighbourhood around the centroid would
    // miss a site sitting inside the far corner of a big face.
    const minIx = Math.floor((Math.min(ax, bx, cx) - WOOD_RADIUS_M) / SITE_CELL_M);
    const maxIx = Math.floor((Math.max(ax, bx, cx) + WOOD_RADIUS_M) / SITE_CELL_M);
    const minIz = Math.floor((Math.min(az, bz, cz) - WOOD_RADIUS_M) / SITE_CELL_M);
    const maxIz = Math.floor((Math.max(az, bz, cz) + WOOD_RADIUS_M) / SITE_CELL_M);

    let painted = false;
    for (let ix = minIx; ix <= maxIx; ix++) {
      for (let iz = minIz; iz <= maxIz; iz++) {
        for (const index of grid.get(cellKey(ix, iz)) ?? []) {
          const site = sites[index]!;
          const under =
            Math.hypot(site.x - centroidX, site.z - centroidZ) <= site.radiusM ||
            pointInTriangle(site.x, site.z, ax, az, bx, bz, cx, cz);
          if (!under) continue;
          // Every site over this face is marked, not just the first: the census
          // asks whether each wood is painted somewhere, and a small wood's only
          // face may already have been claimed by a larger neighbour.
          sitesPainted[index] = 1;
          painted = true;
        }
      }
    }
    if (!painted) continue;

    // The same normal `shadedPlateColours` takes, from the same two edges, with
    // the same "ground faces up" sign fix — the plate is drawn `DoubleSide` and
    // a downward normal would shade every slope backwards.
    const ux = p[t + 3]! - p[t]!, uy = p[t + 4]! - p[t + 1]!, uz = p[t + 5]! - p[t + 2]!;
    const vx = p[t + 6]! - p[t]!, vy = p[t + 7]! - p[t + 1]!, vz = p[t + 8]! - p[t + 2]!;
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    const flip = ny < 0 ? -1 : 1;
    const shaded = shade(tone, flip * nx, flip * ny, flip * nz);
    faces.push(t / 9);
    for (let corner = 0; corner < 3; corner++) {
      colours.push(((shaded >> 16) & 0xff) / 255, ((shaded >> 8) & 0xff) / 255, (shaded & 0xff) / 255);
    }
  }

  if (faces.length === 0) return undefined;
  return { faces: new Uint32Array(faces), colours: new Float32Array(colours), sitesPainted, tone };
}

/**
 * Scatters one plate's canopy paint over a colour array that has just been set
 * to the flat or shaded whole-plate buffer. Idempotent, and reversed simply by
 * setting the whole-plate buffer again without calling this — which is what
 * makes the toggle a swap rather than a rebuild.
 */
export function applyWoodGroundPaint(into: Float32Array, paint: WoodGroundPaint): void {
  for (let i = 0; i < paint.faces.length; i++) {
    const at = paint.faces[i]! * 9;
    for (let k = 0; k < 9; k++) into[at + k] = paint.colours[i * 9 + k]!;
  }
}
